"use client"

import { useEffect, useMemo, useState } from "react"
import {
  Box,
  TextField,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Button,
} from "@mui/material"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"

const servicioMatches = (servicio, search) => {
  if (!search) return true
  const term = search.toLowerCase().trim()
  return servicio?.label?.toLowerCase().includes(term)
}

export default function ServiciosList({
  serviciosDisponibles = [],
  serviciosAsignados = [],
  onServiciosAsignadosChange,
  loading = false,
  bomMontoByKey = {},
  formatMontoServicio = () => "$ 0",
}) {
  const [checkedDisponibles, setCheckedDisponibles] = useState([])
  const [checkedAsignados, setCheckedAsignados] = useState([])
  const [searchServicio, setSearchServicio] = useState("")

  const serviciosAsignadosKeys = useMemo(
    () => new Set(serviciosAsignados.map((servicio) => servicio.key)),
    [serviciosAsignados]
  )

  const serviciosDisponiblesSeleccionables = useMemo(
    () => serviciosDisponibles.filter((servicio) => !serviciosAsignadosKeys.has(servicio.key)),
    [serviciosDisponibles, serviciosAsignadosKeys]
  )

  const serviciosDisponiblesFiltrados = useMemo(
    () => serviciosDisponiblesSeleccionables.filter((servicio) => servicioMatches(servicio, searchServicio)),
    [serviciosDisponiblesSeleccionables, searchServicio]
  )

  const serviciosAsignadosFiltrados = useMemo(
    () => serviciosAsignados.filter((servicio) => servicioMatches(servicio, searchServicio)),
    [serviciosAsignados, searchServicio]
  )

  useEffect(() => {
    const disponiblesSet = new Set(serviciosDisponiblesSeleccionables.map((servicio) => servicio.key))
    setCheckedDisponibles((prev) => prev.filter((key) => disponiblesSet.has(key)))
  }, [serviciosDisponiblesSeleccionables])

  useEffect(() => {
    const asignadosSet = new Set(serviciosAsignados.map((servicio) => servicio.key))
    setCheckedAsignados((prev) => prev.filter((key) => asignadosSet.has(key)))
  }, [serviciosAsignados])

  const updateServiciosAsignados = (nextServicios) => {
    if (typeof onServiciosAsignadosChange === "function") {
      onServiciosAsignadosChange(nextServicios)
    }
  }

  const aplicarMontoBom = (servicio) => {
    const montoBom = Number(bomMontoByKey?.[servicio.key])
    return Number.isFinite(montoBom) ? { ...servicio, montoUnitario: montoBom } : servicio
  }

  const moverADerecha = () => {
    if (checkedDisponibles.length === 0) return
    const mover = serviciosDisponiblesSeleccionables.filter((servicio) =>
      checkedDisponibles.includes(servicio.key)
    )
    if (mover.length === 0) return
    updateServiciosAsignados([...serviciosAsignados, ...mover.map(aplicarMontoBom)])
    setCheckedDisponibles([])
  }

  const moverAIzquierda = () => {
    if (checkedAsignados.length === 0) return
    updateServiciosAsignados(
      serviciosAsignados.filter((servicio) => !checkedAsignados.includes(servicio.key))
    )
    setCheckedAsignados([])
  }

  const moverServicioADerecha = (servicio) => {
    if (!servicio?.key || serviciosAsignadosKeys.has(servicio.key)) return
    updateServiciosAsignados([...serviciosAsignados, aplicarMontoBom(servicio)])
    setCheckedDisponibles((prev) => prev.filter((key) => key !== servicio.key))
  }

  const moverServicioAIzquierda = (servicioKey) => {
    if (!servicioKey) return
    updateServiciosAsignados(serviciosAsignados.filter((servicio) => servicio.key !== servicioKey))
    setCheckedAsignados((prev) => prev.filter((key) => key !== servicioKey))
  }

  return (
    <>
      <TextField
        fullWidth
        size="small"
        label="Buscar servicio"
        value={searchServicio}
        onChange={(e) => setSearchServicio(e.target.value)}
        sx={{ mb: 1 }}
      />

      <Grid container spacing={1} alignItems="center">
        <Grid item xs={12} md={5.5}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Servicios No Asignados
          </Typography>
          <Paper variant="outlined" sx={{ height: 190, overflow: "auto" }}>
            {loading ? (
              <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List dense sx={{ py: 0.25 }}>
                {serviciosDisponiblesFiltrados.length === 0 ? (
                  <ListItemText sx={{ px: 1.5, py: 0.75 }} primary="Sin resultados" />
                ) : (
                  serviciosDisponiblesFiltrados.map((servicio) => (
                    <ListItemButton
                      key={servicio.key}
                      selected={checkedDisponibles.includes(servicio.key)}
                      onClick={() => moverServicioADerecha(servicio)}
                      sx={{ py: 0.3, px: 1.25 }}
                    >
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            lineHeight: 1.2,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {servicio.label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, color: "#ef6c00" }}>
                          {formatMontoServicio(servicio.montoUnitario)}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  ))
                )}
              </List>
            )}
          </Paper>
          <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() =>
                setCheckedDisponibles(serviciosDisponiblesFiltrados.map((servicio) => servicio.key))
              }
            >
              Todos
            </Button>
            <Button size="small" variant="outlined" onClick={() => setCheckedDisponibles([])}>
              Ninguno
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={1}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Asignar">
              <span>
                <IconButton
                  color="primary"
                  onClick={moverADerecha}
                  disabled={checkedDisponibles.length === 0}
                  size="small"
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Quitar">
              <span>
                <IconButton
                  color="primary"
                  onClick={moverAIzquierda}
                  disabled={checkedAsignados.length === 0}
                  size="small"
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Grid>

        <Grid item xs={12} md={5.5}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            Servicios Asignados
          </Typography>
          <Paper variant="outlined" sx={{ height: 190, overflow: "auto" }}>
            <List dense sx={{ py: 0.25 }}>
              {serviciosAsignadosFiltrados.length === 0 ? (
                <ListItemText sx={{ px: 1.5, py: 0.75 }} primary="Sin servicios asignados" />
              ) : (
                serviciosAsignadosFiltrados.map((servicio) => (
                  <ListItemButton
                    key={servicio.key}
                    selected={checkedAsignados.includes(servicio.key)}
                    onClick={() => moverServicioAIzquierda(servicio.key)}
                    sx={{ py: 0.3, px: 1.25 }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          lineHeight: 1.2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {servicio.label}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#ef6c00" }}>
                        {formatMontoServicio(servicio.montoUnitario)}
                      </Typography>
                    </Box>
                  </ListItemButton>
                ))
              )}
            </List>
          </Paper>
          <Box sx={{ display: "flex", gap: 1, mt: 0.75 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setCheckedAsignados(serviciosAsignadosFiltrados.map((servicio) => servicio.key))}
            >
              Todos
            </Button>
            <Button size="small" variant="outlined" onClick={() => setCheckedAsignados([])}>
              Ninguno
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  )
}
